import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import AddonsSelector from "@/components/AddonsSelector";

export default function AddGuestDialog({ open, onOpenChange, activityId, onAdded }) {
    const [form, setForm] = useState({ name: "", email: "", phone: "", note: "" });
    const [transportChoice, setTransportChoice] = useState(null);
    const [accommodationDates, setAccommodationDates] = useState([]);
    const [activity, setActivity] = useState(null);
    const [busy, setBusy] = useState(false);
    const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

    useEffect(() => {
        if (!open || !activityId) return;
        setTransportChoice(null);
        setAccommodationDates([]);
        let alive = true;
        (async () => {
            try {
                const { data } = await api.get(`/activities/${activityId}`);
                if (alive) setActivity(data);
            } catch { /* silent */ }
        })();
        return () => { alive = false; };
    }, [open, activityId]);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
            return toast.error("Name, email and phone are required");
        }
        setBusy(true);
        try {
            const { data } = await api.post(`/activities/${activityId}/guests`, {
                ...form,
                transport_choice: transportChoice,
                accommodation_dates: accommodationDates,
            });
            onAdded?.(data.activity);
            toast.success(data.status === "waitlist" ? "Added to waitlist" : "Guest added");
            setForm({ name: "", email: "", phone: "", note: "" });
            setTransportChoice(null);
            setAccommodationDates([]);
            onOpenChange(false);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not add guest");
        } finally {
            setBusy(false);
        }
    };

    const showAddons = activity && (activity.offer_transport || activity.offer_accommodation);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-md rounded-none border-[#E5E5E3] p-0 shadow-none max-h-[90vh] grid-rows-[1fr_auto] overflow-hidden" data-testid="add-guest-dialog">
                <form onSubmit={submit} className="contents">
                    <div className="p-8 overflow-y-auto">
                        <DialogHeader className="text-left space-y-1">
                            <p className="text-[10px] tracking-[0.2em] uppercase text-[#888885]">New guest</p>
                            <DialogTitle className="font-heading text-2xl font-light tracking-tight text-[#111110]">RSVP someone manually</DialogTitle>
                            <DialogDescription className="text-sm text-[#666664]">Useful for verbal RSVPs or last-minute additions. Capacity is respected — overflow goes to the waitlist.</DialogDescription>
                        </DialogHeader>
                        <div className="mt-6 space-y-4">
                            {[["name","Name"],["email","Email"],["phone","Phone"],["note","Note (optional)"]].map(([k,l]) => (
                                <div key={k}>
                                    <Label className="text-[10px] tracking-[0.2em] uppercase text-[#888885] mb-2 block font-medium">{l}</Label>
                                    {k === "note" ? (
                                        <Textarea value={form[k]} onChange={e => set(k, e.target.value)}
                                            data-testid={`add-guest-${k}`}
                                            className="rounded-none border-[#E5E5E3] resize-none min-h-[64px] focus-visible:ring-1 focus-visible:ring-[#111110]" />
                                    ) : (
                                        <Input value={form[k]} onChange={e => set(k, e.target.value)}
                                            type={k==="email"?"email":k==="phone"?"tel":"text"}
                                            data-testid={`add-guest-${k}`}
                                            className="rounded-none border-[#E5E5E3] h-11 focus-visible:ring-1 focus-visible:ring-[#111110]" />
                                    )}
                                </div>
                            ))}
                            {showAddons && (
                                <AddonsSelector
                                    activity={activity}
                                    transportChoice={transportChoice}
                                    onTransportChoice={setTransportChoice}
                                    accommodationDates={accommodationDates}
                                    onAccommodationDates={setAccommodationDates}
                                    idPrefix="add-guest"
                                />
                            )}
                        </div>
                    </div>
                    <DialogFooter className="border-t border-[#E5E5E3] px-8 py-5 bg-[#F9F9F8] flex-row gap-2 justify-end">
                        <Button variant="outline" type="button" onClick={() => onOpenChange(false)}
                            className="rounded-none border-[#E5E5E3] h-10 text-[#111110] hover:bg-[#F0F0EE]"
                            data-testid="add-guest-cancel">Cancel</Button>
                        <Button type="submit" disabled={busy}
                            className="rounded-none h-10 bg-[#111110] hover:bg-[#333333] text-white px-6"
                            data-testid="add-guest-save">Add to RSVP list</Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
